import { DOCUMENT } from '@angular/common';
import {
    DestroyRef,
    effect,
    inject,
    type Injector,
    runInInjectionContext,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import {
    AppTopBarScopeRegistry,
    type AppTopBarScopePortal,
} from '@quipex/core/layout';
import { assertInjector, injectViewportBreakpoints } from '@quipex/core/utils';
import {
    DialogService,
    injectPopover,
    type PopoverRef,
} from '@quipex/shared/ui';
import { filter, take } from 'rxjs';
import { loadBuildingSelectorPanel } from './load-building-selector-panel';

const SEARCH_SELECTOR = '#building-selector-search';

interface ScopeIdentity {
    readonly buildingId: string | null;
    readonly kind: AppTopBarScopePortal['kind'];
}

export interface BuildingSelectorOpenOptions {
    readonly mode?: 'mine' | 'discover';
    readonly searchTerm?: string;
}

export interface BuildingSelectorLauncher {
    /** Opens or refocuses the building selector for the active route scope. */
    open(
        origin?: HTMLElement,
        options?: BuildingSelectorOpenOptions
    ): Promise<void>;
}

export function injectBuildingSelectorLauncher(
    injector?: Injector
): BuildingSelectorLauncher {
    const resolvedInjector = assertInjector(
        injectBuildingSelectorLauncher,
        injector
    );
    const destroyRef = resolvedInjector.get(DestroyRef);
    const mobile = runInInjectionContext(resolvedInjector, () =>
        injectViewportBreakpoints().lessThan('tablet768')
    );
    const selectorWidth = () => (mobile() ? '100vw' : 'calc(100vw - 2rem)');
    const document = resolvedInjector.get(DOCUMENT);
    const router = resolvedInjector.get(Router);
    const scopeRegistry = resolvedInjector.get(AppTopBarScopeRegistry);
    const popover = injectPopover(resolvedInjector);
    let activeDialog: MatDialogRef<unknown> | undefined;
    let activePopover: PopoverRef | undefined;
    let activeScope: ScopeIdentity | undefined;
    let opening: Promise<void> | undefined;
    let restoreFocus = true;
    let sessionGeneration = 0;
    let dismissalBlocked = false;

    const closeWithoutRestoringFocus = (): void => {
        sessionGeneration += 1;
        restoreFocus = false;
        activeScope = undefined;
        activeDialog?.close(false);
        activePopover?.close(false);
    };

    router.events
        .pipe(
            filter(
                (event): event is NavigationEnd =>
                    event instanceof NavigationEnd
            ),
            takeUntilDestroyed(destroyRef)
        )
        .subscribe(() => closeWithoutRestoringFocus());

    runInInjectionContext(resolvedInjector, () =>
        effect(() => {
            const nextScope = toScopeIdentity(scopeRegistry.activeScope());
            if (
                activeScope &&
                (!nextScope || !sameScope(activeScope, nextScope))
            ) {
                closeWithoutRestoringFocus();
            }
        })
    );

    destroyRef.onDestroy(() => closeWithoutRestoringFocus());
    runInInjectionContext(resolvedInjector, () =>
        effect(() => {
            const width = selectorWidth();
            const height = mobile() ? '100dvh' : '';
            activeDialog?.updateSize(width, height);
        })
    );

    const open = async (
        origin?: HTMLElement,
        options: BuildingSelectorOpenOptions = {}
    ): Promise<void> => {
        if (activeDialog || activePopover) {
            focusSearch(document);
            return;
        }
        if (opening) return opening;

        dismissalBlocked = false;
        const scope = scopeRegistry.activeScope();
        if (!scope) {
            throw new Error(
                'Cannot open the building selector without an active app scope.'
            );
        }

        const focusOrigin = getFocusOrigin(document);
        const scopeIdentity = toScopeIdentity(scope);
        if (!scopeIdentity) return;
        const openingGeneration = sessionGeneration + 1;
        sessionGeneration = openingGeneration;
        activeScope = scopeIdentity;

        if (origin) {
            const componentLoad = loadBuildingSelectorPanel();
            const popoverRef: PopoverRef = popover.open({
                closeOnOutsidePointer: true,
                canDismiss: () => !dismissalBlocked,
                componentInputs: {
                    anchored: true,
                    initialMode: options.mode ?? 'mine',
                    close: () => {
                        if (!dismissalBlocked) popoverRef.close(false);
                    },
                    dismissalBlocked: (blocked: boolean) => {
                        dismissalBlocked = blocked;
                    },
                    currentBuilding: scope.currentBuilding,
                    showAllBuildings: true,
                    scopeKind: scope.kind,
                },
                loadComponent: () => componentLoad,
                origin,
                panelClass: 'building-selector-popover',
            });
            activePopover = popoverRef;
            restoreFocus = true;

            popoverRef
                .afterClosed()
                .pipe(take(1))
                .subscribe(() => {
                    if (activePopover !== popoverRef) return;

                    activePopover = undefined;
                    activeScope = undefined;
                    if (restoreFocus && origin.isConnected) origin.focus();
                });
            opening = componentLoad
                .then(() => undefined)
                .finally(() => {
                    opening = undefined;
                });
            return opening;
        }

        opening = loadBuildingSelectorPanel()
            .then((BuildingSelectorPanel) =>
                runInInjectionContext(resolvedInjector, () => {
                    const currentScope = toScopeIdentity(
                        scopeRegistry.activeScope()
                    );
                    if (
                        openingGeneration !== sessionGeneration ||
                        !currentScope ||
                        !sameScope(scopeIdentity, currentScope)
                    ) {
                        return;
                    }

                    const dialogRef: MatDialogRef<unknown> = inject(
                        DialogService
                    ).openDialog(
                        {
                            component: BuildingSelectorPanel,
                            componentInputs: {
                                anchored: false,
                                initialMode: options.mode ?? 'mine',
                                ...(options.searchTerm
                                    ? { initialSearch: options.searchTerm }
                                    : {}),
                                close: () => {
                                    if (!dismissalBlocked)
                                        dialogRef.close(false);
                                },
                                dismissalBlocked: (blocked: boolean) => {
                                    dismissalBlocked = blocked;
                                    dialogRef.disableClose = blocked;
                                },
                                currentBuilding: scope.currentBuilding,
                                showAllBuildings: false,
                                scopeKind: scope.kind,
                            },
                        },
                        {
                            ariaLabel: 'Choose a building',
                            autoFocus: SEARCH_SELECTOR,
                            disableClose: false,
                            injector: resolvedInjector,
                            maxHeight: '100dvh',
                            maxWidth: '55rem',
                            panelClass: [
                                'building-selector-dialog',
                                'flex-content',
                                'no-padding',
                                'no-scroll',
                            ],
                            restoreFocus: false,
                            width: selectorWidth(),
                            height: mobile() ? '100dvh' : undefined,
                        }
                    ) as MatDialogRef<unknown>;

                    activeDialog = dialogRef;
                    restoreFocus = true;

                    dialogRef
                        .afterClosed()
                        .pipe(take(1))
                        .subscribe(() => {
                            if (activeDialog !== dialogRef) return;

                            activeDialog = undefined;
                            activeScope = undefined;
                            if (restoreFocus && focusOrigin?.isConnected) {
                                focusOrigin.focus();
                            }
                        });
                })
            )
            .finally(() => {
                opening = undefined;
            });

        return opening;
    };

    return { open };
}

function focusSearch(document: Document): void {
    document.querySelector<HTMLElement>(SEARCH_SELECTOR)?.focus();
}

function getFocusOrigin(document: Document): HTMLElement | null {
    const activeElement = document.activeElement;
    const HTMLElementConstructor = document.defaultView?.HTMLElement;

    return HTMLElementConstructor &&
        activeElement instanceof HTMLElementConstructor
        ? activeElement
        : null;
}

function sameScope(left: ScopeIdentity, right: ScopeIdentity): boolean {
    return left.kind === right.kind && left.buildingId === right.buildingId;
}

function toScopeIdentity(
    scope: AppTopBarScopePortal | null
): ScopeIdentity | null {
    if (!scope) return null;

    return {
        buildingId: scope.currentBuilding?.id ?? null,
        kind: scope.kind,
    };
}
